import React, { useState } from "react";
import { Table, Form } from "react-bootstrap";
import { Check, Circle, Close, Delete, Edit, North, South } from "@mui/icons-material";
import TableLoader from "../../../elements/TableLoader";
import OurLink from "../../../elements/OurLink";
import { useParams } from "react-router-dom";
import { getComparator, stableSort } from "../../../../lib/utils";
import { isExecutiveManager, renderUserOptions } from "../../../../lib/userUtils";
import CustomIconButton from "../../../elements/CustomIconButton";
import PaginationComponent from "../../../elements/PaginationComponent";
import PaginationInfo from "../../../elements/PaginationInfo";

const LinkedTestCasesTable = ({
  data,
  editFormData,
  handleDropdownChange,
  handleCheckboxChange,
  headerTabs,
  selectedItems,
  setSelectedItems,
  users,
  loading,
  handleEditClick,
  handleSaveClick,
  setShowConfirm,
  isCompleted,
  setIsEditing,
  handlePageChange,
  totalCount,
  page,
  perPage,
  setPerPage,
}) => {
  const [editingRowId, setEditingRowId] = useState(null);
  const { projectId } = useParams();
  const [orderBy, setOrderBy] = useState('key');
  const [order, setOrder] = useState('asc')

  const handleSelectAll = (checked) => {
    if (checked) {
      const allSelected = data.map((r) => r.id);
      setSelectedItems(allSelected);
      handleEditClick();
    } else {
      setSelectedItems([]);
      setIsEditing(false);
    }
  };

  const handleSave = async () => {
    await handleSaveClick();
    setEditingRowId(null);
  };

  const handleCancel = () => {
    setEditingRowId(null);
  };

  const handleEdit = (id) => {
    setSelectedItems([]);
    setEditingRowId(id);
    handleEditClick();
  };

  const handleDelete = (id) => {
    setSelectedItems([id]);
    setShowConfirm(true);
  };

  const handleSort = (key) => {
    setOrder(order === 'asc' ? 'desc' : 'asc');
    setOrderBy(key);
  };

  return (
    <>
      <div className="d-flex gap-2 align-items-center justify-content-end">
        <PaginationInfo
          currentPage={page}
          perPage={perPage}
          totalCount={totalCount}
          showText=""
          showCenter={false}
          setPerPage={setPerPage}
          showRowCount={true}
        />
      </div>
      <Table striped responsive>
        <thead>
          <tr>
            <th className="text-center">
              <Form.Check
                type="checkbox"
                checked={data.length > 0 && selectedItems.length === data.length}
                disabled={isCompleted || isExecutiveManager()}
                onChange={(e) => handleSelectAll(e.target.checked)}
              />
            </th>
            {headerTabs.map((header) => (
              <th
                key={header.key}
                className={header?.sortable ? 'cursor-pointer' : 'cursor-arrow'}
                title={header.hoverText || ""}
                onClick={header?.sortable ? () => handleSort(header.key) : () => { }}>
                {header.label}{' '}
                {header?.sortable && header.key === orderBy ? order === 'asc' ? <North /> : <South /> : null}
              </th>
            ))}
            <th className="text-center"></th>
          </tr>
        </thead>
        {loading ? (
          <TableLoader colSpan={headerTabs.length + 1} />
        ) : (
          <tbody>
            {data && stableSort(data, getComparator(order, orderBy))?.map((row) => {
              const isEditing = editingRowId === row?.id;
              return (
                <tr key={row?.id}>
                  <td className="text-center">
                    <Form.Check
                      type="checkbox"
                      disabled={isCompleted || isExecutiveManager()}
                      checked={selectedItems.includes(row?.id)}
                      onChange={() => {
                        handleCheckboxChange(row?.id);
                        setEditingRowId(null);
                      }}
                    />
                  </td>
                  <td style={{ whiteSpace: "nowrap" }} className="linked-testcase-table">
                    <OurLink
                      to={`/project/${projectId}/testcases/${row?.testcase?.key}`}
                    >
                      {row?.key}
                    </OurLink>
                  </td>
                  <td className="linked-testcase-table">
                    <span title={row?.description} className="ellipsis">
                      {row?.description.length > 25
                        ? `${row?.description?.substring(0, 25)} ...`
                        : row?.description}
                    </span>
                  </td>
                  {isEditing ? (
                    <>
                      <td width={'12%'}>
                        <Form.Select
                          as="select"
                          size="sm"
                          value={
                            editFormData[row?.id]?.assigned_to ||
                            row?.assigned_to ||
                            ""
                          }
                          onChange={(e) =>
                            handleDropdownChange(e, "assigned_to", row?.id)
                          }
                        >
                          <option value="" disabled>
                            Select Tester
                          </option>
                          {renderUserOptions(users)}
                        </Form.Select>
                      </td>
                    </>
                  ) : (
                    <>
                      <td className="linked-testcase-table">
                        <span className={row?.assignedTo === 'Not Assigned' ? "text-muted" : ""}>{row?.assignedTo}</span>
                      </td>
                    </>
                  )}
                  <td className="linked-testcase-table">
                    <span className={row?.environment === 'None' ? "text-muted" : ""}>{row?.environment}</span>
                  </td>
                  <td className="linked-testcase-table">
                    <span className={row?.iterations === 'None' ? "text-muted" : ""}>{row?.iterations}</span>
                  </td>
                  <td className="linked-testcase-table">{row?.module}</td>
                  <td className="linked-testcase-table">
                    <span className={row?.suites === 'None' ? "text-muted" : ""}>{row?.suites}</span>
                  </td>
                  <td className="linked-testcase-table">
                    {row?.executionStatus === 'No Executions' ? (
                      <span className="text-muted">No Executions</span>
                    ) : (
                      <span
                        title={row?.status?.name}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                        }}>
                        <Circle sx={{ color: row?.executionStatus?.color }} />
                        <span style={{ marginLeft: '5px' }}>{row?.executionStatus?.name}</span>
                      </span>
                    )}
                  </td>
                  <td className="row-action">
                    {isEditing ? (
                      <>
                        <CustomIconButton
                          disableRipple
                          title="Save Changes"
                          onClick={handleSave}
                        >
                          <Check sx={{ color: "green" }} fontSize="small" />
                        </CustomIconButton>

                        <CustomIconButton
                          disableRipple
                          title="Cancel"
                          onClick={handleCancel}
                        >
                          <Close sx={{ color: "grey" }} fontSize="small" />
                        </CustomIconButton>
                      </>
                    ) : isExecutiveManager() || isCompleted ? null : (
                      <>
                        <CustomIconButton
                          disableRipple
                          title="Edit"
                          onClick={() => handleEdit(row?.id)}
                        >
                          <Edit sx={{ color: "var(--secondary-color)" }} fontSize="small" />
                        </CustomIconButton>
                        <CustomIconButton
                          disableRipple
                          title="Remove Test Case"
                          onClick={() => handleDelete(row.id)}
                        >
                          <Delete sx={{ color: "red" }} fontSize="small" />
                        </CustomIconButton>
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
            {data.length === 0 && (
              <tr>
                <td colSpan={headerTabs.length + 3} className="text-center text-muted fs-12">
                  No linked test cases found 
                </td>
              </tr>
            )}
          </tbody>
        )}
      </Table>
      <PaginationComponent
        totalCount={totalCount}
        currentPage={page}
        handlePageChange={handlePageChange}
        perPage={perPage}
      />
    </>
  );
};

export default LinkedTestCasesTable;
